const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers ?? {}) },
  });
  if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
  return res.json();
}

export interface Tenant {
  id: string;
  external_id: string | null;
  source_cart: string;
  plan: string;
  status: string;
  created_at: number;
}

export interface Job {
  id: string;
  template_id: string;
  status: string;
  started_at: number;
  finished_at: number | null;
  row_count: number | null;
  error_summary: string | null;
}

export interface Template {
  id: string;
  tenant_id: string;
  name: string;
  yaml_content: string;
  version: number;
  is_active: number;
  updated_at: number;
}

export interface RunJobResult {
  jobId: string;
  success: boolean;
  orderCount: number;
  rowCount: number;
  deliveryRef?: string;
  error?: string;
  durationMs: number;
}

export interface MappingSuggestion {
  candidates: Array<{
    targetField: string;
    sourceField: string;
    confidence: number;
    rationale: string;
  }>;
  unmappedFields: string[];
  totalTokensUsed: number;
  estimatedCostJpy: number;
}

export const api = {
  // Tenants
  createTenant: (input: { id?: string; sourceCart: string; plan?: string }) =>
    request<{ tenant: Tenant }>('/tenants', { method: 'POST', body: JSON.stringify(input) }),
  getTenant: (id: string) => request<{ tenant: Tenant }>(`/tenants/${id}`),

  // Jobs
  runJob: (input: {
    tenantId: string;
    cartSource: string;
    connector: string;
    delivery: string;
    deliverySettings: unknown;
  }) => request<RunJobResult>('/jobs/run', { method: 'POST', body: JSON.stringify(input) }),

  runTemplate: (input: { tenantId: string; templateId: string }) =>
    request<RunJobResult>('/jobs/run-template', { method: 'POST', body: JSON.stringify(input) }),

  recentJobs: (tenantId: string) =>
    request<{ jobs: Job[] }>(`/jobs/recent?tenantId=${tenantId}`),

  // Templates
  listTemplates: (tenantId: string) =>
    request<{ templates: Template[] }>(`/templates?tenantId=${tenantId}`),

  createTemplate: (input: { tenantId: string; name: string; yamlContent: string }) =>
    request<{ template: Template }>('/templates', { method: 'POST', body: JSON.stringify(input) }),

  // Mappings
  listMappings: (tenantId: string, tableName: string) =>
    request<{ mappings: Array<{ source_key: string; target_key: string }> }>(
      `/mappings?tenantId=${tenantId}&tableName=${tableName}`,
    ),

  upsertMapping: (input: {
    tenantId: string;
    tableName: string;
    sourceKey: string;
    targetKey: string;
  }) => request<{ ok: true }>('/mappings', { method: 'POST', body: JSON.stringify(input) }),

  // AI
  suggestMapping: (input: {
    tenantId: string;
    sourceFields: string[];
    targetSample: string[];
    targetName: string;
  }) => request<MappingSuggestion>('/ai/suggest-mapping', { method: 'POST', body: JSON.stringify(input) }),

  // Registry info
  registryInfo: () =>
    request<{
      cartSources: Array<{ id: string; name: string }>;
      connectors: Array<{ id: string; name: string }>;
      deliveries: Array<{ id: string; name: string }>;
    }>('/registry'),
};
